import { format } from "date-fns";

interface Assignment {
  id: string;
  crew_id: string;
  crew?: { name: string };
  start_time: string;
  end_time: string;
  status: string;
  job?: { property_address: string };
}

interface Crew {
  id: string;
  name: string;
}

interface DayTimelineProps {
  assignments: Assignment[];
  crews: Crew[];
  selectedDate: Date;
}

const CREW_COLORS = [
  "bg-primary/80",
  "bg-secondary/80",
  "bg-accent/80",
  "bg-warning/80",
  "bg-success/80",
];

export function DayTimeline({ assignments, crews, selectedDate }: DayTimelineProps) {
  const hours = Array.from({ length: 15 }, (_, i) => i + 6); // 6 AM to 8 PM

  const getTimePosition = (time: string) => {
    const [hour, minute] = time.split(":").map(Number);
    return ((hour - 6) * 60 + minute) / (15 * 60) * 100;
  };

  const getDuration = (start: string, end: string) => {
    const [startHour, startMin] = start.split(":").map(Number);
    const [endHour, endMin] = end.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return ((endMinutes - startMinutes) / (15 * 60)) * 100;
  };

  return (
    <div className="premium-card p-6">
      <h3 className="font-semibold mb-4">
        Day View: {format(selectedDate, "MMMM d, yyyy")}
      </h3>
      
      <div className="relative">
        {/* Time header */}
        <div className="flex border-b border-border">
          <div className="w-32 flex-shrink-0" />
          <div className="flex-1 flex">
            {hours.map((hour) => (
              <div 
                key={hour} 
                className="flex-1 text-center text-xs text-muted-foreground pb-2 border-l border-border"
              >
                {hour > 12 ? `${hour - 12}PM` : `${hour}AM`}
              </div>
            ))}
          </div>
        </div>

        {/* Crew rows */}
        {crews.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No crews to display
          </div>
        ) : (
          <div className="space-y-2 mt-2">
            {crews.map((crew, crewIndex) => {
              const crewAssignments = assignments.filter(a => a.crew_id === crew.id);
              const colorClass = CREW_COLORS[crewIndex % CREW_COLORS.length];

              return (
                <div key={crew.id} className="flex items-center min-h-[60px] border-b border-border/50 last:border-0">
                  <div className="w-32 flex-shrink-0 pr-4">
                    <p className="text-sm font-medium truncate">{crew.name}</p>
                  </div>
                  <div className="flex-1 relative h-14">
                    {/* Hour dividers */}
                    {hours.map((hour) => (
                      <div 
                        key={hour}
                        className="absolute top-0 bottom-0 border-l border-border/30"
                        style={{ left: `${((hour - 6) / 15) * 100}%` }}
                      />
                    ))}
                    
                    {/* Assignments */}
                    {crewAssignments.map((assignment) => (
                      <div
                        key={assignment.id}
                        className={`absolute top-1 bottom-1 ${colorClass} rounded px-2 py-1 text-xs text-white overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer`}
                        style={{
                          left: `${getTimePosition(assignment.start_time)}%`,
                          width: `${getDuration(assignment.start_time, assignment.end_time)}%`,
                        }}
                      >
                        <p className="font-semibold truncate">{assignment.job?.property_address}</p>
                        <p className="text-[10px] opacity-90">
                          {assignment.start_time} - {assignment.end_time}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
